import { ButtonSize, ButtonVariant } from "@galaxy-io/dls/buttons/Button";
import EmptyState from "@galaxy-io/dls/feedback/EmptyState";
import Flex, { FlexDirection } from "@galaxy-io/dls/layout/Flex";
import Link from "@galaxy-io/dls/links/Link";
import Timeline from "@galaxy-io/dls/timeline/Timeline";
import Widget, { WidgetVariant } from "@galaxy-io/dls/widget/Widget";
import { type ActivityItem, activityVerb } from "@gtm/contracts";
import { queryHref, Link as RouterLink, usePathname, useUrlState } from "@/app/navigation";
import { Button } from "@/components/ui/button";
import { SourceMarkdown } from "@/components/workspace/source-markdown";
import { ActivitySignalChip, activityIcon } from "@/features/activity/activity-signal-chip";
import { sourceLabel } from "@/lib/source-label";
import { date, safeUrl } from "@/lib/utils";
import { CommentContext } from "./comment-context";

export function ActivityTimeline({
  items,
  hasNext,
  loading,
  onNext,
}: {
  items: ActivityItem[];
  hasNext: boolean;
  loading: boolean;
  onNext: () => void;
}) {
  const { params } = useUrlState();
  const path = usePathname();
  return (
    <Flex
      direction={FlexDirection.COLUMN}
      grow={1}
      minHeight={0}
      overflow="auto"
      padding={24}
      gap={16}
      aria-label="Person activity timeline"
      aria-busy={loading}
    >
      {items.length ? (
        <Timeline
          aria-label="Activity"
          items={items.map((item) => {
            const url = safeUrl(item.url);
            const at = item.occurred_at ?? item.observed_at;
            const href = queryHref(path, new URLSearchParams(params), {
              activityId: item.id,
              personId: null,
              person: null,
              personTab: null,
              runId: null,
            });
            return {
              id: item.id,
              label: `${item.name || item.login || "Unknown person"} ${activityVerb(item.type, item.kind, item.baseline)}${item.context ? ` · ${sourceLabel(item.provider, item.context)}` : ""}`,
              description: item.title || undefined,
              icon: activityIcon(item.type),
              time: date(at, true),
              dateTime: at,
              footer: (
                <Flex direction={FlexDirection.COLUMN} gap={8} minWidth={0}>
                  <Flex gap={8}>
                    <ActivitySignalChip activity={item} />
                    <Button
                      as={RouterLink}
                      href={href}
                      label="View signal"
                      size={ButtonSize.SMALL}
                      variant={ButtonVariant.TERTIARY}
                    />
                  </Flex>
                  {url && (
                    <Link href={url} isExternal>
                      {url}
                    </Link>
                  )}
                  <CommentContext context={item.commentContext} />
                  {item.body && (
                    <Widget variant={WidgetVariant.SECONDARY}>
                      <SourceMarkdown content={item.body} />
                    </Widget>
                  )}
                </Flex>
              ),
            };
          })}
        />
      ) : (
        <EmptyState
          header="No activity yet"
          description="Activity appears here as connected sources collect signals."
        />
      )}
      {hasNext && (
        <Button
          label="Load more"
          isLoading={loading}
          variant={ButtonVariant.SECONDARY}
          isDisabled={loading}
          onClick={onNext}
        />
      )}
    </Flex>
  );
}
